import type { OrderMappingBulkPlan, OrderMappingTag } from "@/lib/manufacturing/orderMappingBulk";
import { orderMappingTagKey, orderMappingTagLabel } from "@/lib/manufacturing/orderMappingBulk";

const inputClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900";

export function InventoryOutlookOrderMappingBulk({
  tagQuery,
  suggestions,
  selected,
  channels,
  tagKeyByChannel,
  summary,
  skipped,
  busy,
  canSave,
  onTagQuery,
  onAdd,
  onRemove,
  onAssignChannel,
  onMatch,
  onSaveAll,
}: {
  tagQuery: string;
  suggestions: OrderMappingTag[];
  selected: OrderMappingTag[];
  channels: ReadonlyArray<{ channel: string; count: number }>;
  tagKeyByChannel: Readonly<Record<string, string>>;
  summary: string;
  skipped: string[];
  busy: boolean;
  canSave: boolean;
  onTagQuery: (value: string) => void;
  onAdd: (tag: OrderMappingTag) => void;
  onRemove: (tag: OrderMappingTag) => void;
  onAssignChannel: (channel: string, tagKey: string) => void;
  onMatch: () => void;
  onSaveAll: () => void;
}) {
  return (
    <div className="space-y-2 rounded-xl border border-gray-200 p-3 dark:border-zinc-700">
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Search tags on these one-offs. Each tag you add keeps only the rows that carry it. Then point each channel at the tag that should pick its finish. A channel you leave blank is not matched.
      </p>
      <input
        className={`${inputClass} w-full`}
        value={tagQuery}
        placeholder="Search tags, such as series or trim color"
        aria-label="Search one-off tags"
        onChange={(e) => onTagQuery(e.target.value)}
      />
      {tagQuery.trim() && suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {suggestions.slice(0, 12).map((tag) => (
            <button
              key={orderMappingTagKey(tag)}
              type="button"
              className="rounded-md border border-gray-200 px-2 py-1 text-sm dark:border-zinc-700"
              onClick={() => onAdd(tag)}
            >
              {orderMappingTagLabel(tag)}
            </button>
          ))}
        </div>
      ) : null}
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selected.map((tag) => (
            <button
              key={orderMappingTagKey(tag)}
              type="button"
              className="rounded-md border border-gray-900 px-2 py-1 text-sm dark:border-zinc-100"
              onClick={() => onRemove(tag)}
            >
              {orderMappingTagLabel(tag)} ×
            </button>
          ))}
        </div>
      ) : null}
      {selected.length > 0 && channels.length > 0 ? (
        <div className="space-y-1">
          {channels.map((row) => (
            <label key={row.channel || "none"} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-[8rem]">
                {row.channel || "No channel"} · {row.count}
              </span>
              <select
                className={inputClass}
                aria-label={`Match tag for ${row.channel || "parts with no channel"}`}
                value={tagKeyByChannel[row.channel] ?? ""}
                onChange={(e) => onAssignChannel(row.channel, e.target.value)}
              >
                <option value="">Don’t match</option>
                {selected.map((tag) => (
                  <option key={orderMappingTagKey(tag)} value={orderMappingTagKey(tag)}>
                    {orderMappingTagLabel(tag)}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || !Object.values(tagKeyByChannel).some(Boolean)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold dark:border-zinc-600"
          onClick={onMatch}
        >
          Match finishes
        </button>
        <button
          type="button"
          disabled={busy || !canSave}
          className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
          onClick={onSaveAll}
        >
          Save all matches
        </button>
      </div>
      {summary ? <p className="text-sm text-gray-800 dark:text-zinc-200">{summary}</p> : null}
      {skipped.length > 0 ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Left unsaved because the serials do not agree on one SKU: {skipped.join(", ")}.
        </p>
      ) : null}
    </div>
  );
}

export function orderMappingBulkSummary(
  tags: readonly OrderMappingTag[],
  plans: readonly OrderMappingBulkPlan[],
  materialLabel: (materialId: string) => string,
): string {
  if (tags.length === 0 || plans.length === 0) return "";
  const when = tags.map((tag) => orderMappingTagLabel(tag)).join(" and ");
  const lines = plans.map((plan) => `${plan.use_code} → ${materialLabel(plan.material_id)}`);
  return `When ${when}: ${lines.join("; ")}.`;
}
